import React from 'react';
import { Link } from 'react-router-dom';
import { EMPTY, formatDay, formatInt } from '../format';

export function Breadcrumbs({ items }) {
  if (!items?.length) return null;
  return (
    <nav className="re-crumbs" aria-label="Breadcrumb">
      <ol>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={item.path}>
              {last ? (
                <span aria-current="page">{item.name}</span>
              ) : (
                <Link to={item.path}>{item.name}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function Section({ id, title, kicker, aside, children, className = '' }) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <section className={`re-section ${className}`.trim()} id={id} aria-labelledby={headingId}>
      <header className="re-section__head">
        <div>
          {kicker ? <p className="re-kicker">{kicker}</p> : null}
          <h2 id={headingId}>{title}</h2>
        </div>
        {aside ? <div className="re-section__aside">{aside}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** Secondary figures. Items whose value is null or EMPTY are dropped. */
export function StatGrid({ items }) {
  const visible = (items || []).filter((item) => item.value != null && item.value !== EMPTY);
  if (!visible.length) return null;
  return (
    <dl className="re-stats">
      {visible.map((item) => (
        <div className="re-stat" key={item.label}>
          <dt>{item.label}</dt>
          <dd>
            <span className="re-stat__value">{item.value}</span>
            {item.note ? <span className="re-stat__note">{item.note}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function MethodologyNote({ anchor, children }) {
  return (
    <p className="re-footnote">
      {children}{' '}
      <Link to={`/methodology${anchor ? `#${anchor}` : ''}`}>How this is measured</Link>
    </p>
  );
}

export function FreshnessLine({ period, count, countLabel = 'sales with a published guide' }) {
  const parts = [];
  if (period?.to) parts.push(`Data to ${formatDay(period.to)}`);
  if (count != null) parts.push(`${formatInt(count)} ${count === 1 ? countLabel.replace(/^sales/, 'sale') : countLabel}`);
  return (
    <p className="re-freshness">
      {parts.join(' · ')}
      {parts.length ? ' · ' : ''}
      <Link to="/methodology">Methodology</Link>
    </p>
  );
}

export function Notice({ children, tone = 'info' }) {
  return (
    <div className={`re-notice re-notice--${tone}`} role="note">
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <rect x="7.25" y="7" width="1.5" height="5" rx="0.75" fill="currentColor" />
        <circle cx="8" cy="4.75" r="1" fill="currentColor" />
      </svg>
      <p>{children}</p>
    </div>
  );
}

export function SegmentedControl({ label, options, value, onChange }) {
  return (
    <div className="re-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={value === option.value ? 'is-active' : ''}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
